import { UAParser } from 'ua-parser-js';
import EnvironmentHelper from '../EnvironmentHelper/EnvironmentHelper';
import { safeGetError } from '@/functions';

const ua = new UAParser();

const UNKNOWN = 'unknown';

/**
 * @name Logger
 * @description General utility logger to send logs and metrics to Metro, New Relic, and Grafana.
 * the intent is to keep redshift metrics (increment) separate from new relic log entries (log)
 * separate from health metrics (yardstick)
 */

// TODOS: Add Debug library, add batching, document when to use metro vs yardstick,
// document constructor params, especially the required params (appName, xsrc, and env),
// can env handle dashes? (bleeding-edge)
class Logger {
  /**
   * @param {import('./Logger').MetricDetails} metricDetails
   */
  constructor(metricDetails, throwErrors = false) {
    ['appName', 'xsrc', 'env'].forEach((requiredProperty) => {
      if (!(requiredProperty in metricDetails)) {
        throw new Error(`Missing property for logger: ${requiredProperty}`);
      }
    });

    this.xsrc = metricDetails.xsrc;
    this.throwErrors = throwErrors;

    this.baseMetric = {
      app: {
        name: metricDetails.appName,
        environment: metricDetails.env,
      },
      device: {
        os: {
          name: this.os,
          architecture: UNKNOWN,
          version: ua.getOS().version ?? UNKNOWN,
        },
        model: 'unknown_device',
        platform: {
          name: ua.getBrowser().name?.toLowerCase() ?? UNKNOWN,
          architecture: UNKNOWN,
          version: ua.getBrowser().version ?? UNKNOWN,
        },
      },
      sessionId: metricDetails.sessionId ?? UNKNOWN,
      user: {
        accountStatus: metricDetails.user?.accountStatus ?? UNKNOWN,
        clientId: metricDetails.user?.clientId ?? UNKNOWN,
        email: metricDetails.user?.email ?? UNKNOWN,
        subscriptionId: metricDetails.user?.subscriptionId ?? UNKNOWN,
      },
    };
  }

  /**
   * Sends off a redshift metric via metro
   * Usage: Increment a feature's usage.
   * @param {string | sting[]} metricName
   */
  increment(metricName) {
    const names = Array.isArray(metricName) ? metricName : [metricName];

    const payload = {
      ...this.baseMetric,
      names,
    };

    console.info('increment', names);

    const url = new URL('/metric', EnvironmentHelper.getMetroUrl());
    return fetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    }).catch((error) => {
      console.error('logger.increment', safeGetError(error));
      if (this.throwErrors) {
        throw error;
      }
    });
  }

  /**
   * Sends off a log message to New Relic via metro
   * @param {string} message The message to log.
   * @param {object} [details] Any additional details you would like
   * to provide. For errors, use safeGetError()
   * @param  {LogType} [logType] 'error' | 'info' | 'warn' | 'debug'

   * @returns: {Promise<void>}
   */
  log(message, details = {}, logType = 'info') {
    const payload = {
      ...this.baseMetric,
      message,
      detail: details, // metro endpoint expects detail, not details
      logType,
    };

    // the log type is either warn or error, which is valid for eslint.
    // eslint-disable-next-line no-console
    console[logType](message, details);

    const url = new URL('/log', EnvironmentHelper.getMetroUrl());
    return fetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify(payload, null, 0),
      headers: { 'Content-Type': 'application/json' },
    }).catch((error) => {
      console.error('logger.log', safeGetError(error));
      if (this.throwErrors) {
        throw error;
      }
    });
  }

  error(message, details) {
    this.log(message, details, 'error');
  }

  warn(message, details) {
    this.log(message, details, 'warn');
  }

  info(message, details) {
    this.log(message, details, 'info');
  }

  debug(message, details) {
    this.log(message, details, 'debug');
  }

  /**
   * Sends a metric to grafana via yardstick
   * @param {string} metric
   * @param {number} value
   * @param {string} type 'increment' | 'timing'
   * @returns {Promise<void>}
   */
  yardstick(metric, value, type) {
    if (type === 'timing' && (value === null || value === undefined)) {
      throw new Error('Value must be specified if yardstick type is "timing"');
    }

    // eslint-disable-next-line no-param-reassign
    value ??= 1;

    const url = new URL(
      `/feature/${this.xsrc}/${metric}/${type}`,
      EnvironmentHelper.getYardstickUrl(),
    );
    const payload = {
      environment: EnvironmentHelper.getEnvironment(),
      xsrc: this.xsrc,
      value,
    };

    console.info('yardstick', payload);

    return fetch(url.toString(), {
      body: JSON.stringify(payload, null, 0),
      cache: 'no-cache',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      keepalive: false,
      method: 'POST',
      mode: 'cors',
    }).catch((error) => {
      this.log('Error sending metric', 'error', {
        url,
        payload,
        error: safeGetError(error),
      });
    });
  }

  /**
   * @returns {string}
   */
  /* eslint-disable-next-line class-methods-use-this */
  get os() {
    const MACOS = 'macos';
    const name = ua.getOS().name ?? 'unknown';

    const nameMap = {
      mac: MACOS,
      'mac os': MACOS,
      // eslint-disable-next-line camelcase
      mac_os: MACOS,
    };

    if (name !== 'unknown' && name in nameMap) {
      return nameMap[name];
    }

    return name.toLowerCase();
  }
}

export default Logger;
